// Shared activate / delete mutations for profile releases.
//
// Both the legacy /releases/profiles list and the new Chromium > major >
// Profiles tab need the same activate + delete semantics, including the
// fleet-aware "is this thing actually installed somewhere?" delete dialog.
// Lifting them here means the two surfaces can't drift on toast copy,
// invalidation keys, or error handling.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface ActivateResult {
	success?: boolean;
	[key: string]: unknown;
}

interface DeleteResult {
	success?: boolean;
	[key: string]: unknown;
}

interface ApiError {
	error?: { message?: string };
}

async function postActivate(id: string): Promise<ActivateResult> {
	const res = await fetch(
		`/api/releases/profiles/${encodeURIComponent(id)}/activate`,
		{ method: "POST", credentials: "same-origin" },
	);
	const data = (await res.json()) as ActivateResult & ApiError;
	if (!res.ok) {
		throw new Error(data.error?.message ?? `activate ${res.status}`);
	}
	return data;
}

async function deleteRelease({
	id,
	force,
}: {
	id: string;
	force: boolean;
}): Promise<DeleteResult> {
	const qs = force ? "?force=1" : "";
	const res = await fetch(
		`/api/releases/profiles/${encodeURIComponent(id)}${qs}`,
		{ method: "DELETE", credentials: "same-origin" },
	);
	const data = (await res.json()) as DeleteResult & ApiError;
	if (!res.ok) {
		throw new Error(data.error?.message ?? `delete ${res.status}`);
	}
	return data;
}

export function useProfileReleaseMutations(opts?: {
	onActivated?: () => void;
	onDeleted?: () => void;
}) {
	const queryClient = useQueryClient();

	const activate = useMutation({
		mutationFn: postActivate,
		onSuccess: () => {
			toast.success("Activated");
			queryClient.invalidateQueries({ queryKey: ["profile-releases"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
			opts?.onActivated?.();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Activate failed"),
	});

	const remove = useMutation({
		mutationFn: deleteRelease,
		onSuccess: () => {
			toast.success("Deleted");
			queryClient.invalidateQueries({ queryKey: ["profile-releases"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
			opts?.onDeleted?.();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});

	return { activate, remove };
}
